// Calendar views (TW-3456). Both audiences get a month-grid calendar: the club sees its own
// submitted trips (plus its still-pending proposal deadlines), the back office sees the same
// cross-club, filterable by the club switcher. There is no calendar endpoint — the data is the
// RLS-scoped trip list (`GET /api/trips`) plus the pending-deadline feed
// (`GET /api/proposals/calendar-deadlines`, the same items the ops radar plots). These pure folds
// turn those two lists into the day-bucketed grid the web surface renders, with one definition
// shared by the web and its tests. `todayIso` is passed in (never read from the clock) so the
// result is deterministic and testable.

import type { TripStatus } from "./constants";
import type { ProposalDeadlineRadarItem } from "./proposal";
import type { TripDto } from "./trips";

/** A trip plotted on the calendar — opens its trip detail on click. */
export interface CalendarTripEntry {
  kind: "trip";
  /** The trip to open when the entry is clicked. */
  tripId: string;
  clubId: string;
  /** ISO `YYYY-MM-DD` the entry plots on (the trip's travel start). */
  date: string;
  reference: string;
  destinationCity: string | null;
  status: TripStatus;
}

/** A still-pending proposal deadline plotted on the calendar — opens its trip on click. */
export interface CalendarDeadlineEntry {
  kind: "deadline";
  tripId: string;
  clubId: string;
  /** ISO `YYYY-MM-DD` the deadline falls on. */
  date: string;
  tripReference: string | null;
  /** The `proposal.dl*` i18n label key for a standard deadline; null for a custom one. */
  labelKey: string | null;
  /** The literal, operator-entered label for a custom deadline (shown verbatim); null for a standard one. */
  label: string | null;
}

export type CalendarEntry = CalendarTripEntry | CalendarDeadlineEntry;

/**
 * Colour tone for a calendar entry, driving its pill colour and the legend:
 * `event` (a trip on its travel date), `deadline` (an upcoming pending deadline), or
 * `overdue` (a pending deadline whose date has already passed).
 */
export type CalendarEntryTone = "event" | "deadline" | "overdue";

/**
 * Classify an entry for colour-coding. Trips are always `event`; a pending deadline is `overdue`
 * once its date is before `todayIso`, otherwise `deadline`. Dates are zero-padded `YYYY-MM-DD`, so
 * the comparison is a plain string compare (no `Date` parsing / timezone drift). `todayIso` is
 * passed in rather than read from the clock so this stays pure and testable.
 */
export function calendarEntryTone(entry: CalendarEntry, todayIso: string): CalendarEntryTone {
  if (entry.kind === "trip") return "event";
  return entry.date.slice(0, 10) < todayIso.slice(0, 10) ? "overdue" : "deadline";
}

/** One cell of the month grid: a day with its entries, or a leading/trailing padding cell. */
export interface CalendarCell {
  /** Day-of-month 1..31, or null for a padding cell outside the month. */
  day: number | null;
  /** ISO `YYYY-MM-DD` for the cell, or null for a padding cell. */
  date: string | null;
  /** True when the cell is the day matching `todayIso`. */
  isToday: boolean;
  /** Entries falling on this day, trips first then deadlines (see {@link buildCalendarMonth}). */
  entries: CalendarEntry[];
}

/** A built month: the year/month plus a Monday-first run of cells (padding + each day). */
export interface CalendarMonth {
  /** Full year, e.g. 2026. */
  year: number;
  /** Month index, 0 (January) … 11 (December). */
  month: number;
  cells: CalendarCell[];
}

/** Map a trip to its calendar entry, or null when it has no travel start date to plot on. */
export function tripCalendarEntries(trips: readonly TripDto[]): CalendarTripEntry[] {
  const entries: CalendarTripEntry[] = [];
  for (const trip of trips) {
    if (!trip.travelStartDate) continue;
    entries.push({
      kind: "trip",
      tripId: trip.id,
      clubId: trip.clubId,
      date: trip.travelStartDate.slice(0, 10),
      reference: trip.reference,
      destinationCity: trip.destinationCity,
      status: trip.status,
    });
  }
  return entries;
}

/** Map the pending-deadline feed to calendar entries (one per still-pending deadline). */
export function deadlineCalendarEntries(
  items: readonly ProposalDeadlineRadarItem[],
): CalendarDeadlineEntry[] {
  return items.map((item) => ({
    kind: "deadline",
    tripId: item.tripId,
    clubId: item.clubId,
    date: item.date.slice(0, 10),
    tripReference: item.tripReference,
    labelKey: item.labelKey,
    label: item.label,
  }));
}

/** Zero-padded `YYYY-MM-DD` for a year + 0-based month + day, built without `Date` (tz-safe). */
function isoDate(year: number, month0: number, day: number): string {
  const mm = String(month0 + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

/**
 * Bucket the entries into a Monday-first month grid for the given 0-based `month`. Leading padding
 * cells fill the week before the 1st; each subsequent cell is a real day carrying the entries whose
 * `date` falls on it (matched by the `YYYY-MM-DD` prefix, so no `Date` parsing / timezone drift).
 * Within a cell, trips come before deadlines, each then ordered by reference for a stable render.
 * `todayIso` marks the current day; pass it in rather than reading the clock so this stays pure.
 */
export function buildCalendarMonth(
  year: number,
  month: number,
  entries: readonly CalendarEntry[],
  todayIso: string,
): CalendarMonth {
  // Monday-first leading-pad count: JS getDay() is Sunday=0, so shift to Monday=0.
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = todayIso.slice(0, 10);

  const byDay = new Map<number, CalendarEntry[]>();
  for (const entry of entries) {
    const [ey, em, ed] = entry.date.slice(0, 10).split("-").map(Number);
    if (ey !== year || em !== month + 1 || !ed) continue;
    const bucket = byDay.get(ed);
    if (bucket) bucket.push(entry);
    else byDay.set(ed, [entry]);
  }

  const cells: CalendarCell[] = [];
  for (let i = 0; i < firstWeekday; i += 1) {
    cells.push({ day: null, date: null, isToday: false, entries: [] });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = isoDate(year, month, day);
    const dayEntries = (byDay.get(day) ?? []).slice().sort(compareEntries);
    cells.push({ day, date, isToday: date === today, entries: dayEntries });
  }
  return { year, month, cells };
}

/** Stable order within a day: trips before deadlines, then by reference. */
function compareEntries(a: CalendarEntry, b: CalendarEntry): number {
  if (a.kind !== b.kind) return a.kind === "trip" ? -1 : 1;
  const ra = a.kind === "trip" ? a.reference : (a.tripReference ?? "");
  const rb = b.kind === "trip" ? b.reference : (b.tripReference ?? "");
  return ra.localeCompare(rb);
}
